import classnames from "classnames";
import * as React from "react";
import { resizeWatcher } from "../lib/resize-watcher";
import { Button } from "../ui/Button.component";
import { LeftArrowIcon } from "../components/icons/LeftArrowIcon.component";
import { RightArrowIcon } from "../components/icons/RightArrowIcon.component";
import "./Slider.style.scss";

interface SliderProps {
    name?: string;
    children: React.ReactNode;
}

interface SliderState {
    contentLeft: number;
    contentWidth: number;
    viewportWidth: number;
    viewportRight: number;
    viewportLeft: number;
    showNext: boolean;
    lastItem?: Element;
    showPrev: boolean;
}

export class Slider extends React.Component<SliderProps, SliderState> {
    private elementRef: React.RefObject<HTMLUListElement>;
    private viewportRef: React.RefObject<HTMLElement>;
    mounted = false;

    constructor(props: SliderProps) {
        super(props);
        this.state = {
            contentLeft: 0,
            contentWidth: 0,
            viewportWidth: 0,
            viewportRight: 0,
            viewportLeft: 0,
            showNext: false,
            showPrev: false,
        };
        this.elementRef = React.createRef();
        this.viewportRef = React.createRef();
    }

    componentDidMount() {
        this.mounted = true;

        this.initializeView();

        resizeWatcher.onWidthChange.subscribe(this.initializeView);
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    initializeView = () => {
        if (this.elementRef.current && this.viewportRef.current) {
            const items = Array.from(this.elementRef.current.children);

            if (items.length) {
                const contentRect = this.elementRef.current.getBoundingClientRect();
                const viewportRect = this.viewportRef.current.getBoundingClientRect();

                const lastItem = items[items.length - 1];

                this.setState({
                    showNext: lastItem.getBoundingClientRect().right > viewportRect.right,
                    contentWidth: contentRect.width,
                    viewportWidth: viewportRect.width,
                    viewportRight: viewportRect.right,
                    viewportLeft: viewportRect.left,
                    lastItem,
                    showPrev: false,
                });
            }
        }
    };

    showPrevious = () => this.state.contentLeft < 0;

    slideLeft = () => {
        const { contentLeft, contentWidth } = this.state;

        if (!this.mounted || !this.showPrevious()) {
            return;
        }

        this.setState({
            contentLeft: Math.min(contentLeft + contentWidth, 0),
            showNext: true,
            showPrev: false,
        });
    };

    slideRight = () => {
        const { lastItem, viewportWidth, contentWidth, contentLeft: curContentLeft } = this.state;
        let { showNext, showPrev } = this.state;

        if (!this.mounted || !lastItem || !this.state.showNext) {
            return;
        }

        const minLeft = (lastItem.getBoundingClientRect().right - viewportWidth - curContentLeft) * -1;

        let contentLeft = curContentLeft - contentWidth;
        showNext = true;
        // showPrev = true;

        if (contentLeft <= minLeft) {
            contentLeft = minLeft;
            showNext = false;
            showPrev = true;
        }

        this.setState({ contentLeft, showNext, showPrev });
    };

    render() {
        const sliderClass = classnames("component-slider", {
            [`slider-${this.props.name}`]: this.props.name,
        });

        const contentStyle = {
            transform: `translateX(${this.state.contentLeft}px)`,
        };

        const children = React.Children.toArray(this.props.children);
        const leftArrowClass = classnames("slider-action", {
            "disable-button": !this.state.showPrev,
        });
        const rightArrowClass = classnames("slider-action", {
            "disable-button": !this.state.showNext,
        });

        return (
            <div id="slider-wrapper" className={sliderClass}>
                <section className="arrow-section">
                    <Button click={this.slideLeft} size="small" className={leftArrowClass} label="Slide Content Left">
                        <LeftArrowIcon width={32} height={32} />
                    </Button>

                    <Button
                        click={this.slideRight}
                        size="small"
                        className={rightArrowClass}
                        label="Slide Content Right"
                    >
                        <RightArrowIcon width={32} height={32} />
                    </Button>
                </section>

                <section className="slider-viewport" ref={this.viewportRef}>
                    <ul className="slider-content" ref={this.elementRef} style={contentStyle}>
                        {children.map((child, index) => (
                            <li key={index}>{child}</li>
                        ))}
                    </ul>
                </section>
            </div>
        );
    }
}

export default Slider;
